import { Text, Avatar, AvatarProps } from "rizzui";
import cn from "../utils/class-names";

interface AvatarCardProps {
  src: string;
  name: string;
  className?: string;
  nameClassName?: string;
  avatarProps?: AvatarProps;
  description?: React.ReactNode;
}

/**
 * rizzui's own `Avatar` derives its fallback initials from the raw first character of each of the
 * first two words (its `w()` in `dist/chunk-*.js`), with no filtering — a name is arbitrary,
 * caller-typed text (a brand, category, product, customer or user's), so a name starting with a
 * punctuation or markup character (`<script>...`) renders that character as the initial. The
 * visible name text itself is unaffected and stays exactly what was typed (React already escapes
 * it) — this only cleans up the two-letter badge derived from it.
 */
function safeInitials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return (
    words
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase() || "?"
  );
}

export default function AvatarCard({
  src,
  name,
  className,
  description,
  avatarProps,
  nameClassName,
}: AvatarCardProps) {
  return (
    <figure className={cn("flex items-center gap-3", className)}>
      <Avatar name={name} src={src} initials={safeInitials(name)} {...avatarProps} />
      <figcaption className="grid gap-0.5">
        <Text
          className={cn(
            "font-lexend text-sm font-medium text-gray-900 dark:text-gray-700",
            nameClassName
          )}
        >
          {name}
        </Text>
        {description && (
          <Text className="text-[13px] text-gray-500">{description}</Text>
        )}
      </figcaption>
    </figure>
  );
}
